/** @jsxImportSource @emotion/react */
import { faAngleRight } from "@fortawesome/pro-regular-svg-icons";
import { useState } from "react";
import useConfig from "../../../actions/Tenants/config/configHook";
import { useUserPreferences } from "../../../hooks";
import { getFilledArrayOrDefault } from "../../../utils";
import { Button } from "../../Helper/Button";
import { useSwitchTabContext } from "./fields/useSwitchTab";
import StartCheckFromOverviewModal from "./StartCheckFromOverviewModal";

/**
 * Renders one "Start <checkType> check (N selected)" button per tab.
 * Opens the modal in selection mode so the user can run the check on the selected layers.
 */
export default function StartCheckFromOverviewBulkButtons() {
    const config = useConfig();
    const { activeTab, selectedLayersArray, setSelectedLayers, layers: layerList } = useSwitchTabContext();
    const [userPreferences] = useUserPreferences();
    const selectedFruitTypes = getFilledArrayOrDefault(userPreferences?.fruitTypes);
    const selectedFruitType = selectedFruitTypes.length === 1 ? selectedFruitTypes[0] : null;
    const layers = getFilledArrayOrDefault(layerList?.results);

    const [modalCheckType, setModalCheckType] = useState<string | null>(null);

    const selectedLayerObjects = layers.filter((l) => selectedLayersArray.includes(l.id));
    const firstSelected = selectedLayerObjects[0];
    const hasSelection = selectedLayerObjects.length > 0;

    // * Create a layer stub to get the eligible check types
    const layerStub = hasSelection
        ? firstSelected
        : {
            type: activeTab?.layer_type ?? null,
            fruit_type: selectedFruitType,
        };

    const eligibleLocations = config.get_check_types_config(layerStub);
    const visibleLocations = eligibleLocations.filter((l: any) => getFilledArrayOrDefault(l.start_on_overview_tabs).includes(activeTab?.value));
    const startCheckTypes = visibleLocations.map((l: any) => l.value);

    const handleClose = () => setModalCheckType(null);

    const handleSuccess = () => {
        setSelectedLayers({});
    };

    if (startCheckTypes.length === 0) {
        return null;
    }

    return (
        <>
            {startCheckTypes.map((checkTypeValue) => {
                const loc = config.check_locations.find((c: any) => c.value === checkTypeValue);
                const label = loc?.text ?? checkTypeValue;
                const n = selectedLayerObjects.length;
                return (
                    <Button
                        key={checkTypeValue}
                        color={n > 0 ? "primary" : "secondary"}
                        disabled={!hasSelection}
                        icon={faAngleRight}
                        onClick={() => setModalCheckType(checkTypeValue)}
                    >
                        {label} check{n > 0 ? ` ${n}` : ""}
                    </Button>
                );
            })}
            {modalCheckType && firstSelected && (
                <StartCheckFromOverviewModal
                    isOpen={Boolean(modalCheckType)}
                    onClose={handleClose}
                    layer={firstSelected}
                    checkLocationValue={modalCheckType}
                    groupItems={selectedLayerObjects}
                    selectionMode
                    onSuccess={handleSuccess}
                />
            )}
        </>
    );
}
